import { useEffect, useState } from "react";
import { askrClients } from "providers/AskrProvider";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { ReportListStyles } from "../../components/ReportsPage/reportsPageStyles";
import { Navigate, useLocation, useNavigate } from "react-router";
import ReportItemAccordion from "../../components/ReportsPage/ReportItemAccordion";
import { useReportsPropsList } from "components/ReportsPage/useReportsPropsList";
import { getIsCypressRunning, useMount } from "reports/constants/constants";
import { ApolloProvider } from "@apollo/client";
import {
  CustomReportWithSchedule,
  ReportItemProps,
  ReportScheduleType,
} from "components/ReportsPage/reportPageTypes";
import { useSearchParam } from "react-use";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useIsDarkMode } from "store/atoms";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import PerformanceSummaryListItem from "components/ReportsPage/PerformanceSummaryListItem";
import { ReportTypesEnum } from "types/enums";
import { format } from "date-fns";
import { ShiftSegment } from "types/types";
import { useReportConfigTimeZone } from "components/ReportsPage/reportPageUtils";
import Spinner from "components/SimpleComponents/Spinner/Spinner";

const ReportsListingPageContent = ({
  customReportItems,
  customReportItemsWithSchedule,
}: {
  customReportItems?: ReportItemProps[];
  /**
   * customReportItemsWithSchedule follows a schedule will show up as a
   * list of reports on this page based on a time interval (eos, daily, weekly)
   */
  customReportItemsWithSchedule?: CustomReportWithSchedule[];
}) => {
  const [
    {
      general: { reportsEnabled },
    },
  ] = useFlightConfig();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  // if we land on this page with explicitly undefined workSchedule param, set to 1
  useEffect(() => {
    if (search.includes("workSchedule=undefined")) {
      navigate(`${pathname}?workSchedule=1`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // separate filter state for calendar (instead of using selectedDay) so we don't start filtered to today
  const [calendarSelectedDay, setCalendarSelectedDay] =
    useState<ShiftSegment | null>(null);

  const reportsPropsList = useReportsPropsList({
    customReportItems,
    customReportItemsWithSchedule,
    calendarSelectedDay,
  });
  const section = useSearchParam("section");
  const sectionIndex = section ? parseInt(section, 10) : -1;

  // Only have expanded if there is one section in reports page
  const initialExpandState = reportsPropsList.length === 1;

  // wait a second for LD flags to arrive before redirecting (else redirects when navigating directly by url to e.g. /ravenjest/reports)
  const [waitedForLDFlags, setWaitedForLDFlags] = useState(false);
  useMount(() => {
    const ldFlagTimer = window.setTimeout(
      () => setWaitedForLDFlags(true),
      2000
    );
    return () => {
      clearTimeout(ldFlagTimer);
    };
  });

  const shouldRedirectHome =
    (waitedForLDFlags && !getIsCypressRunning() && !reportsEnabled) ||
    reportsPropsList.length < 1;

  const [isDarkMode] = useIsDarkMode();
  const isAdminUser = useIsAdmin();
  const timeZoneConfig = useReportConfigTimeZone();
  return !timeZoneConfig ? (
    <div style={{ padding: "2em" }}>
      <Spinner />
    </div>
  ) : shouldRedirectHome ? (
    <Navigate replace={true} to="/" />
  ) : (
    <ReportListStyles $isDarkMode={isDarkMode}>
      {reportsPropsList.map((reportProps, index) => (
        <ReportItemAccordion
          key={reportProps.reportTitle || ""}
          initialExpandState={
            initialExpandState ||
            sectionIndex == index ||
            section == reportProps.reportTitle
          }
          {...reportProps}
          {...{ calendarSelectedDay, setCalendarSelectedDay }}
        />
      ))}
      {/* for Raven emails, show a Monthly Report section */}
      {isAdminUser
        ? (() => {
            const startOfLastMonth = new Date(
              new Date().getFullYear(),
              new Date().getMonth() - 1,
              1
            ).getTime();
            const endOfLastMonth = new Date(
              new Date().getFullYear(),
              new Date().getMonth(),
              1
            ).getTime();
            return (
              <>
                <h1 style={{ padding: "1em" }}>— Monthly Report —</h1>
                <PerformanceSummaryListItem
                  portalReportPath={`eos/All?ge=${startOfLastMonth}&lt=${endOfLastMonth}&hideLocalBanner=true&showMore=true&customTitle=${encodeURIComponent(
                    "All - Monthly"
                  )}`}
                  customReportTitle={`Monthly — ${format(
                    startOfLastMonth,
                    "MMMM yyyy"
                  )}`}
                  customReportSubtitle={`${new Date(
                    startOfLastMonth
                  )} — ${new Date(endOfLastMonth)}`}
                  {...{
                    ge: startOfLastMonth,
                    lt: endOfLastMonth,
                    name: "Monthly",
                    reportType: ReportTypesEnum.custom,
                    reportScheduleType: ReportScheduleType.MONTHLY,
                  }}
                />
              </>
            );
          })()
        : null}
    </ReportListStyles>
  );
};

export const ReportsListingPage = props => {
  const ckey = useCustomerKey();
  return !ckey ? null : (
    <ApolloProvider client={askrClients.get(ckey)}>
      <ReportsListingPageContent {...props} />
    </ApolloProvider>
  );
};
